import React, { useState, useEffect, useRef, useCallback } from 'react'
import { 
  Plus, History, LoaderCircle, Sparkles, 
  Brush, Type
} from 'lucide-react'
import RightPanelHeader from './RightPanelHeader'
import ToolSelector from './tools/ToolSelector'
import MaskCreationForm from './masks/MaskCreationForm'
import MaskList from './masks/MaskList'
import InpaintingForm from './masks/InpaintingForm'
import VersionHistory from './versions/VersionHistory'
import AdjustmentSlider from './adjustments/AdjustmentSlider'
import ConfirmationModal from '../../shared/ConfirmationModal'
import useToastStore from '../../../stores/toastStore'
import useLoadingStore from '../../../stores/loadingStore'
import useEditStore from '../../../controllers/EditController'
import masksApi from '../../../services/masksApi'
import generationsApi from '../../../services/generationsApi'
import MaskPreview from '../MaskPreview'
import useLocalStorage from '../../../hooks/useLocalStorage'
import Breadcrumb from './shared/Breadcrumb'
import useMaskStore from '../../../stores/maskStore'
import { Spinner } from '../../ui/spinner'
import { useVersionStatus } from '../../../hooks/useVersionStatus'

const DEFAULT_ADJUSTMENTS = {
  exposure: 0,
  contrast: 0,
  highlights: 0,
  shadows: 0
}

const DEFAULT_BRUSH_SETTINGS = {
  size: 20,
  smoothness: 0.5,
  mode: 'paint' // or 'erase'
}

// Add this custom hook for polling
function useStatusPolling(generationId) {
  const [status, setStatus] = useState('processing')
  const [error, setError] = useState(null)

  const checkStatus = useCallback(async () => {
    try {
      const response = await fetch(`/api/generations/status/${generationId}`)
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error(data.error)
      }

      setStatus(data.status)
      setError(data.error)
      
      return data.status
    } catch (err) {
      setError(err.message)
      return 'failed'
    }
  }, [generationId])

  useEffect(() => {
    if (!generationId || status === 'completed' || status === 'failed') {
      return
    }

    let timeoutId
    let mounted = true

    const poll = async () => {
      const currentStatus = await checkStatus()
      
      if (!mounted) return
      
      if (currentStatus === 'processing') {
        timeoutId = setTimeout(poll, 3000) // Poll every 3 seconds
      }
    }

    poll()

    return () => {
      mounted = false
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [generationId, status, checkStatus])

  return { status, error }
}

// Update the version item component
function ToolVersionItem({ version }) {
  const { status, error, isLoading } = useVersionStatus(version._id)
  
  return (
    <div className="flex flex-col gap-2 p-3 rounded-lg border bg-white/50 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium">Version {version.versionNumber}</span>
          {(isLoading || status === 'processing') && (
            <Spinner className="w-4 h-4" />
          )}
        </div>
        <div className="text-sm text-gray-500">
          {version.toolMetadata.toolType}
        </div>
      </div>
      
      {/* Tool-specific metadata */}
      <div className="text-sm text-gray-600">
        {version.toolMetadata.params.prompt && (
          <div className="truncate">
            Prompt: {version.toolMetadata.params.prompt}
          </div>
        )}
        {version.toolMetadata.params.strength && (
          <div>
            Strength: {version.toolMetadata.params.strength}
          </div>
        )}
      </div>

      {status === 'failed' && (
        <div className="text-sm text-red-500">
          Error: {error}
        </div>
      )}
    </div>
  )
}

// Update the versions list component
function VersionsList({ versions }) {
  return (
    <div className="space-y-3">
      {versions.map((version) => (
        <ToolVersionItem 
          key={version._id} 
          version={version}
        />
      ))}
    </div>
  )
}

// Add tool action handlers
function useToolActions(generationId) {
  const [isProcessing, setIsProcessing] = useState(false)

  const handleInpainting = async (params) => {
    setIsProcessing(true)
    try {
      const newVersion = await generationsApi.createToolVersion(generationId, {
        toolType: 'inpainting',
        ...params
      })
      return newVersion
    } finally {
      setIsProcessing(false)
    }
  }

  // Add other tool handlers as needed

  return {
    isProcessing,
    handleInpainting
  }
}

const RightPanel = ({ 
  selectedImage, 
  onAdjustmentsChange, 
  onSaveVersion, 
  isVisible, 
  imagePreviewRef,
  onBrushSettingsChange,
  isPainting,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onDeleteGroup,
  onMaskSelect,
  onPreviewToggle,
  showMaskPreview,
  selectedMaskId
}) => {
  const featureFlags = {
    generativeInpainting: true
  }
  const [adjustments, setAdjustments] = useState(DEFAULT_ADJUSTMENTS)
  const [showVersions, setShowVersions] = useState(false)
  const [versions, setVersions] = useState([])
  const [activeVersion, setActiveVersion] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedTool, setSelectedTool] = useState(null)
  const [brushSettings, setBrushSettings] = useState(DEFAULT_BRUSH_SETTINGS)
  const [brushMode, setBrushMode] = useState('brush') // 'brush' or 'erase'
  const [inpaintingMode, setInpaintingMode] = useState(null) // 'brush' or 'text'
  const [inpaintingStep, setInpaintingStep] = useState('initial') // 'initial', 'create', 'select'
  const [existingMasks, setExistingMasks] = useState([]) // This will be populated from backend later
  const [maskName, setMaskName] = useState('')
  const [maskPrompt, setMaskPrompt] = useState('')
  const [maskNegativePrompt, setMaskNegativePrompt] = useState('')
  const addToast = useToastStore(state => state.addToast)
  const featureFlagSliders = false;
  const featureFlagSaveVersion = false;
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isCreatingMask, setIsCreatingMask] = useState(false)
  const [createdMaskId, setCreatedMaskId] = useState(null)
  const [generatingMaskId, setGeneratingMaskId] = useState(null)
  const [viewingMasks, setViewingMasks] = useState(false)
  const [masks, setMasks] = useState([])
  const [isLoadingExistingMasks, setIsLoadingExistingMasks] = useState(false)
  const [adjustmentFactor, setAdjustmentFactor] = useState(-15)
  const currentImageIdRef = useRef(null)
  const [inpaintingParams, setInpaintingParams] = useState({
    inputPrompt: '',
    negativePrompt: '',
    steps: 50,
    guidanceScale: 7.5,
    seed: ''
  })
  const [isSubmittingInpainting, setIsSubmittingInpainting] = useState(false)
  const [navigationStack, setNavigationStack] = useState([])
  const navigationPath = useMaskStore(state => state.navigationPath)
  const setNavigationPath = useMaskStore(state => state.setNavigationPath)

  // Add ref to track current polling state
  const pollingRef = useRef(false)
  const timeoutRef = useRef(null)
  const pollingIntervalRef = useRef(null)

  // Add new ref for scrollable container
  const scrollContainerRef = useRef(null)

  // Add scroll to top function
  const scrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }
  }, [])

  useEffect(() => {
    if (selectedImage?.id) {
      setInpaintingStep('initial')
      setSelectedTool(null)
      setNavigationStack([])
      setNavigationPath([])
      setAdjustments(DEFAULT_ADJUSTMENTS)
    }
  }, [selectedImage?.id])

  // Add validation function
  const validateInpaintingParams = (params) => {
    if (!params.inputPrompt.trim()) {
      addToast('Please enter an input prompt', 'error')
      return false
    }
    if (params.steps < 1 || params.steps > 500) {
      addToast('Steps must be between 1 and 500', 'error')
      return false
    }
    if (params.guidanceScale < 1 || params.guidanceScale > 20) {
      addToast('Guidance scale must be between 1 and 20', 'error')
      return false
    }
    return true
  }

  // Add submission handler
  const handleInpaintingSubmit = async () => {
    if (!selectedImage?.id || !selectedMaskId || !inpaintingParams.inputPrompt) {
      addToast({
        title: 'Validation Error',
        description: 'Missing required fields for inpainting',
        type: 'error'
      })
      return
    }

    setIsSubmittingInpainting(true)
    try {
      // Create tool version with maskId
      const newVersion = await generationsApi.createToolVersion(selectedImage.id, {
        toolType: 'inpainting',
        maskId: selectedMaskId,  // Add maskId to the request
        params: {
          prompt: inpaintingParams.inputPrompt,
          negativePrompt: inpaintingParams.negativePrompt,
          strength: 0.75,
          model: inpaintingParams.model || 'flux1d',
          steps: inpaintingParams.steps,
          guidanceScale: inpaintingParams.guidanceScale,
          seed: inpaintingParams.seed || Math.floor(Math.random() * 1000000)
        }
      })

      // Add new version to list
      setVersions(prev => [...prev, newVersion])
      setActiveVersion(newVersion)

      // Show success message
      addToast({
        title: 'Inpainting Started',
        description: 'Your image is being processed. You can track its progress in the versions list.',
        type: 'success'
      })

      // Reset form
      setInpaintingParams({
        inputPrompt: '',
        negativePrompt: '',
        steps: 50,
        guidanceScale: 7.5,
        seed: '',
        model: 'flux1d'
      })
      setInpaintingStep('initial')
      setSelectedTool(null)
    } catch (error) {
      console.error('Inpainting error:', error)
      addToast({
        title: 'Inpainting Failed',
        description: error?.message || 'Failed to start inpainting. Please try again.',
        type: 'error'
      })
    } finally {
      setIsSubmittingInpainting(false)
    }
  }

  // Reset adjustments when switching images
  useEffect(() => {
    setAdjustments(DEFAULT_ADJUSTMENTS)
    // Load versions for current image
    if (selectedImage?.id) {
      const savedVersions = JSON.parse(localStorage.getItem(`versions-${selectedImage.id}`)) || []
      setVersions(savedVersions)
    }
  }, [selectedImage?.id])

  const handleSaveVersion = async () => {
    const setIsSavingVersion = useLoadingStore.getState().setIsSavingVersion
    
    try {
      setIsSavingVersion(true)
      // Get adjusted image data
      const imageData = imagePreviewRef.current.getAdjustedImageData()

      // Save version
      const result = await generationsApi.saveVersion(
        selectedImage.id,
        imageData
      )

      if (result.success) {
        const newVersion = {
          ...result.version,
          parentId: selectedImage.id
        }
        
        // Update versions for this specific image
        const updatedVersions = [...versions, newVersion]
        setVersions(updatedVersions)
        localStorage.setItem(`versions-${selectedImage.id}`, JSON.stringify(updatedVersions))
        
        // Reset adjustments and notify parent
        setAdjustments(DEFAULT_ADJUSTMENTS)
        onAdjustmentsChange?.(DEFAULT_ADJUSTMENTS)
        onSaveVersion?.(newVersion)
      }
    } catch (error) {
      console.error('Failed to save version:', error)
      // Add user feedback here
    } finally {
      setIsSavingVersion(false)
    }
  }

  const handleVersionSelect = (version) => {
    setAdjustments(DEFAULT_ADJUSTMENTS)
    onAdjustmentsChange?.(DEFAULT_ADJUSTMENTS)
  }

  const handleAdjustmentChange = (type, value) => {
    const newAdjustments = { ...adjustments, [type]: value }
    setAdjustments(newAdjustments)
    onAdjustmentsChange?.(newAdjustments)
  }

  // Handle brush settings changes
  const handleBrushSettingChange = (setting, value) => {
    const newSettings = { ...brushSettings, [setting]: value }
    setBrushSettings(newSettings)
    onBrushSettingsChange?.(newSettings)
  }

  // Update brush mode handler
  const handleBrushModeChange = (mode) => {
    setBrushMode(mode)
    onBrushSettingsChange({ 
      action: 'mode',
      mode: mode 
    })
  }

  // Update tool selection handler
  const handleToolSelect = (tool) => {
    console.log('Tool selected:', tool)
    
    if (tool === selectedTool) return
    
    setSelectedTool(tool)
    setInpaintingStep('initial') // Reset to initial step
    setInpaintingMode(null)
  }

  // Add handler for mask creation/selection
  const handleMaskAction = (action) => {
    if (action === 'create') {
      setInpaintingStep('create')
      const currentItems = getBreadcrumbItems()
      setNavigationStack([...currentItems, { id: 'create-mask', label: 'Create Mask' }])
    } else if (action === 'select') {
      setInpaintingStep('select')
      setViewingMasks(true)
      const currentItems = getBreadcrumbItems()
      setNavigationStack([...currentItems, { id: 'existing-masks', label: 'Existing Masks' }])
    }
  }

  // Add handler for inpainting mode selection
  const handleInpaintingModeSelect = (mode) => {
    // Set the mode first
    setInpaintingMode(mode)
    // Keep the step as 'create' since we're creating a mask
    setInpaintingStep('create')
    
    if (mode === 'brush') {
      setTimeout(() => {
        onBrushSettingsChange({ action: 'start' })
        setBrushMode('brush')
        useEditStore.getState().setIsBrushing(true)
      }, 100)
    }
  }

  // Update the cancel/done handlers
  const handleCancel = () => {
    setSelectedTool(null)
    setInpaintingStep('initial')
    setInpaintingMode(null)
    onBrushSettingsChange({ action: 'stop' })
    useEditStore.getState().setIsBrushing(false)
  }

  const loadMasks = useCallback(async () => {
    try {
      const result = await masksApi.listMasks(
        selectedImage.id,
        selectedImage.versionId || selectedImage.id
      )
      setMasks(result.masks)
    } catch (error) {
      console.error('Failed to load masks:', error)
      addToast('Failed to load masks', 'error')
    }
  }, [selectedImage])

  const handleDone = async () => {
    setIsCreatingMask(true)
    try {
      const result = await masksApi.createMask({
        generationId: selectedImage.id,
        versionId: selectedImage.versionId || selectedImage.id,
        name: maskName,
        type: inpaintingMode === 'brush' ? 'brush' : 'description',
        imageData: inpaintingMode === 'brush' ? await onBrushSettingsChange({ action: 'export' }) : undefined,
        prompt: maskPrompt,
        negativePrompt: maskNegativePrompt,
        adjustmentFactor
      })

      // Switch to masks view
      setViewingMasks(true)
      await loadMasks() // Load all masks including the new one

      if (result.mask.status === 'generating') {
        setGeneratingMaskId(result.mask.id)
        pollMaskStatus(result.mask.id)
      } else {
        setCreatedMaskId(result.mask.id)
        addToast('Mask created successfully', 'success')
      }
    } catch (error) {
      console.error('Failed to create mask:', error)
      addToast(error.message || 'Failed to create mask', 'error')
    } finally {
      setIsCreatingMask(false)
    }
  }

  const pollMaskStatus = async (maskId) => {
    const stopPolling = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      pollingRef.current = false
    }

    const startPolling = async () => {
      // If already polling, don't start another poll
      if (pollingRef.current) {
        return
      }

      try {
        pollingRef.current = true

        if (currentImageIdRef.current !== selectedImage?.id) {
          stopPolling()
          console.debug('Stopped polling mask status - image changed')
          return
        }

        const result = await masksApi.checkStatus(maskId)
        
        if (result.status === 'completed') {
          if (currentImageIdRef.current === selectedImage?.id) {
            if (generatingMaskId === maskId) {
              setGeneratingMaskId(null)
              setCreatedMaskId(maskId)
              addToast('Mask created successfully', 'success')
            }
            await loadMasks()
          }
          stopPolling()
        } else if (result.status === 'failed') {
          if (currentImageIdRef.current === selectedImage?.id) {
            if (generatingMaskId === maskId) {
              setGeneratingMaskId(null)
            }
            addToast(result.error || 'Failed to generate mask', 'error')
            await loadMasks()
          }
          stopPolling()
        } else {
          // Continue polling if still on same image
          if (currentImageIdRef.current === selectedImage?.id) {
            // Wait at least 2 seconds before next poll
            timeoutRef.current = setTimeout(() => {
              pollingRef.current = false
              startPolling()
            }, 2000)
          }
        }
      } catch (error) {
        console.error('Failed to check mask status:', error)
        if (currentImageIdRef.current === selectedImage?.id) {
          if (generatingMaskId === maskId) {
            setGeneratingMaskId(null)
          }
          addToast(error.message || 'Failed to check mask status', 'error')
          await loadMasks()
        }
        // On error, wait longer before retrying
        timeoutRef.current = setTimeout(() => {
          pollingRef.current = false
          startPolling()
        }, 5000)
      }
    }

    // Start initial polling
    startPolling()

    // Cleanup function
    return () => {
      stopPolling()
    }
  }

  // Add a cleanup effect for polling
  useEffect(() => {
    return () => {
      currentImageIdRef.current = null // This will stop all polling
    }
  }, [])

  // Update the export handler in RightPanel
  const handleExport = () => {
    onBrushSettingsChange({ action: 'export' })
  }

  // Update delete handler
  const handleDelete = async () => {
    try {
      await onDeleteGroup(selectedImage.groupId)
      setShowDeleteModal(false)
    } catch (error) {
      console.error('Failed to delete photo:', error)
    }
  }

  // Add function to load existing masks
  const loadExistingMasks = useCallback(async () => {
    if (!selectedImage) return
    
    setIsLoadingExistingMasks(true)
    try {
      const result = await masksApi.listMasks(
        selectedImage.id,
        selectedImage.versionId || selectedImage.id
      )
      
      setExistingMasks(result.masks)
      setMasks(result.masks)

      // Start polling for any generating masks
      const generatingMasks = result.masks.filter(mask => 
        mask.status === 'generating' && mask.replicatePredictionId
      )
      generatingMasks.forEach(mask => {
        console.debug('Starting polling for newly loaded generating mask:', mask._id)
        pollMaskStatus(mask._id)
      })
    } catch (error) {
      console.error('Failed to load existing masks:', error)
      addToast('Failed to load masks', 'error')
    } finally {
      setIsLoadingExistingMasks(false)
    }
  }, [selectedImage, addToast])

  // Load masks when tool is selected
  useEffect(() => {
    if (selectedTool === 'inpainting') {
      loadExistingMasks()
    }
  }, [selectedTool, loadExistingMasks])

  // Update getBreadcrumbItems to handle all steps
  const getBreadcrumbItems = () => {
    const baseItems = [{ id: 'tools', label: 'Tools' }]
    
    if (selectedTool === 'inpainting') {
      baseItems.push({ id: 'inpainting', label: 'Generative Inpainting' })
      
      if (inpaintingStep === 'create') {
        baseItems.push({ id: 'create-mask', label: 'Create Mask' })
        
        // Add mode-specific breadcrumb
        if (inpaintingMode === 'text') {
          baseItems.push({ id: 'text-mask', label: 'Write Description' })
        } else if (inpaintingMode === 'brush') {
          baseItems.push({ id: 'brush-mask', label: 'Draw Mask' })
        }
      } else if (inpaintingStep === 'select' || viewingMasks) {
        baseItems.push({ id: 'existing-masks', label: 'Existing Masks' })
      }
      
      // Add Use Mask breadcrumb when in prompt step
      if (inpaintingStep === 'prompt') {
        if (!baseItems.some(item => item.id === 'existing-masks')) {
          baseItems.push({ id: 'existing-masks', label: 'Existing Masks' })
        }
        baseItems.push({ id: 'use-mask', label: 'Use Mask' })
      }
    }
    
    return baseItems
  }

  // Update handleBack to use breadcrumb navigation
  const handleBack = () => {
    const currentItems = getBreadcrumbItems()
    if (currentItems.length > 1) {
      // Navigate to the previous item in the breadcrumb
      handleNavigation(currentItems[currentItems.length - 2].id)
    }
  }

  // Update handleNavigation to handle all steps
  const handleNavigation = (targetStep) => {
    console.log('Navigation to:', targetStep)
    const currentItems = getBreadcrumbItems()
    const targetIndex = currentItems.findIndex(item => item.id === targetStep)
    
    if (targetIndex >= 0) {
      // Pop back to the target step
      const newItems = currentItems.slice(0, targetIndex + 1)
      setNavigationStack(newItems)
      
      // Update UI state based on target
      switch (targetStep) {
        case 'tools':
          setSelectedTool(null)
          setInpaintingStep('initial')
          setInpaintingMode(null)
          setViewingMasks(false)
          break
          
        case 'inpainting':
          setSelectedTool('inpainting')
          setInpaintingStep('initial')
          setInpaintingMode(null)
          setViewingMasks(false)
          break
          
        case 'create-mask':
          setSelectedTool('inpainting')
          setInpaintingStep('create')
          setInpaintingMode(null)
          setViewingMasks(false)
          break
          
        case 'existing-masks':
          setSelectedTool('inpainting')
          setInpaintingStep('select')
          setInpaintingMode(null)
          setViewingMasks(true)
          break

        case 'text-mask':
          setSelectedTool('inpainting')
          setInpaintingStep('create')
          setInpaintingMode('text')
          setViewingMasks(false)
          break

        case 'brush-mask':
          setSelectedTool('inpainting')
          setInpaintingStep('create')
          setInpaintingMode('brush')
          setViewingMasks(false)
          break

        case 'use-mask':
          setSelectedTool('inpainting')
          setInpaintingStep('prompt')
          setInpaintingMode('existing')
          setViewingMasks(false)
          break
          
        default:
          console.warn('Unknown navigation step:', targetStep)
      }
    }
  }

  // Update handleUseMask to handle navigation
  const handleUseMask = () => {
    console.log('handleUseMask called')
    // Set the params first
    setInpaintingParams({
      ...inpaintingParams,
      inputPrompt: selectedImage?.prompt || '',
      negativePrompt: selectedImage?.negativePrompt || '',
      steps: 50,
      guidanceScale: 7.5,
      seed: ''
    })
    // Explicitly set the step to 'prompt'
    setInpaintingStep('prompt')
    // Update navigation stack
    const newItems = [
      { id: 'tools', label: 'Tools' },
      { id: 'inpainting', label: 'Generative Inpainting' },
      { id: 'existing-masks', label: 'Existing Masks' },
      { id: 'use-mask', label: 'Use Mask' }
    ]
    setNavigationStack(newItems)
    
    // Add small delay to ensure component renders first
    setTimeout(() => {
      scrollToTop()
    }, 100)
  }

  // Update renderToolControls to use new breadcrumb system
  const renderToolControls = () => {
    const breadcrumbItems = getBreadcrumbItems()
    
    if (selectedTool === 'inpainting') {
      // Handle the prompt step first
      if (inpaintingStep === 'prompt') {
        return (
          <InpaintingForm
            inpaintingParams={inpaintingParams}
            onParamsChange={setInpaintingParams}
            onSubmit={handleInpaintingSubmit}
            onBack={handleBack}
            isSubmitting={isSubmittingInpainting}
            LoadingIndicator={Spinner}
          />
        )
      }

      if (viewingMasks) {
        return (
          <MaskList
            masks={masks}
            selectedMaskId={selectedMaskId}
            showMaskPreview={showMaskPreview}
            onMaskSelect={onMaskSelect}
            onPreviewToggle={onPreviewToggle}
            onUseMask={handleUseMask}
            onNavigate={handleNavigation}
            breadcrumbItems={breadcrumbItems}
          />
        )
      }

      // Render text-based mask creation form
      if (inpaintingMode === 'text') {
        return (
          <MaskCreationForm
            maskName={maskName}
            maskPrompt={maskPrompt}
            maskNegativePrompt={maskNegativePrompt}
            adjustmentFactor={adjustmentFactor}
            isCreatingMask={isCreatingMask}
            onMaskNameChange={setMaskName}
            onMaskPromptChange={setMaskPrompt}
            onMaskNegativePromptChange={setMaskNegativePrompt}
            onAdjustmentFactorChange={setAdjustmentFactor}
            onCancel={handleBack}
            onSubmit={handleDone}
            onNavigate={handleNavigation}
            breadcrumbItems={breadcrumbItems}
            LoadingIndicator={Spinner}
          />
        )
      }

      // Initial mask selection screen
      if (inpaintingStep === 'initial') {
        return (
          <div className="space-y-4 mt-4">
            <Breadcrumb 
              items={breadcrumbItems}
              onNavigate={handleNavigation}
            />
            <p className="text-sm text-gray-400 mb-4">
              Choose how you want to proceed:
            </p>
            <button
              onClick={() => handleMaskAction('create')}
              className="w-full p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors mb-3 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-600 rounded-md">
                  <Plus className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium">Create new mask</div>
                  <div className="text-sm text-gray-400">
                    Start with a fresh mask
                  </div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => handleMaskAction('select')}
              disabled={isLoadingExistingMasks ? false : masks.length === 0}
              className={`w-full p-4 rounded-lg text-left transition-colors
                ${isLoadingExistingMasks 
                  ? 'bg-gray-700' 
                  : masks.length === 0 
                    ? 'bg-gray-800 cursor-not-allowed opacity-50' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-600 rounded-md">
                  <History className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium">Use existing mask</div>
                  <div className="text-sm text-gray-400">
                    {isLoadingExistingMasks ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" />
                        <span>Checking for masks...</span>
                      </div>
                    ) : (
                      masks.length === 0 
                        ? 'No masks available for this image'
                        : `${masks.length} mask${masks.length === 1 ? '' : 's'} available`
                    )}
                  </div>
                </div>
              </div>
            </button>
          </div>
        )
      }

      // Mask creation options screen
      if (inpaintingStep === 'create' && !inpaintingMode) {
        return (
          <div className="space-y-4 mt-4">
            <Breadcrumb 
              items={breadcrumbItems}
              onNavigate={handleNavigation}
            />
            <p className="text-sm text-gray-400 mb-4">
              Choose how you want to create your mask:
            </p>

            <button
              // onClick={() => handleInpaintingModeSelect('brush')}
              className="w-full p-4 bg-gray-700/50 rounded-lg cursor-not-allowed text-left relative"
            >
              <div className="absolute top-4 left-4">
                <div className="flex items-center gap-1.5 bg-gray-800/80 backdrop-blur-sm text-[10px] py-0.5 px-1.5 rounded-full tracking-wide font-medium text-gray-300">
                  <Sparkles className="w-3 h-3" />
                  <span>COMING SOON</span>
                </div>
              </div>
              <div className="flex items-center gap-3 opacity-50 mt-6">
                <div className="p-2 bg-gray-600 rounded-md">
                  <Brush className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium">Draw with brush</div>
                  <div className="text-sm text-gray-400">
                    Paint over areas you want to change
                  </div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => handleInpaintingModeSelect('text')}
              className="w-full p-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-600 rounded-md">
                  <Type className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium">Write a description</div>
                  <div className="text-sm text-gray-400">
                    Describe the areas you want to change
                  </div>
                </div>
              </div>
            </button>

            <button
              onClick={handleCancel}
              className="w-full p-2 bg-gray-700 rounded hover:bg-gray-600 mt-4"
            >
              Cancel
            </button>
          </div>
        )
      }
    }
    return null
  }

  // Add an effect to log state changes
  useEffect(() => {
    console.log('State changed:', {
      inpaintingStep,
      inpaintingMode,
      viewingMasks,
      selectedMaskId
    })
  }, [inpaintingStep, inpaintingMode, viewingMasks, selectedMaskId])

  // Add scroll helper function
  const scrollToPreview = useCallback((previewElement) => {
    if (!previewElement || !scrollContainerRef.current) return

    const container = scrollContainerRef.current
    const containerRect = container.getBoundingClientRect()
    const previewRect = previewElement.getBoundingClientRect()
    
    // Calculate if preview is below visible area
    if (previewRect.bottom > containerRect.bottom) {
      const scrollOffset = previewRect.bottom - containerRect.bottom + 40 // Add padding
      container.scrollBy({
        top: scrollOffset,
        behavior: 'smooth'
      })
    }
  }, [])

  // Add effect to handle mask selection
  useEffect(() => {
    if (selectedMaskId) {
      const previewElement = document.querySelector(`[data-mask-id="${selectedMaskId}"]`)
      if (previewElement) {
        scrollToPreview(previewElement)
      }
    }
  }, [selectedMaskId, scrollToPreview])

  if (!selectedImage) return null

  return (
    <>
      <div 
        className={`fixed right-0 top-8 bottom-24 w-80 bg-gray-800 text-white flex flex-col
          transform transition-all duration-200 ease-in-out ${
            isVisible ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        <RightPanelHeader
          selectedImage={selectedImage}
          selectedTool={selectedTool}
          inpaintingMode={inpaintingMode}
          inpaintingStep={inpaintingStep}
          versions={versions}
          showMoreMenu={showMoreMenu}
          onMoreMenuToggle={setShowMoreMenu}
          onShowDeleteModal={setShowDeleteModal}
          onBack={handleBack}
        />

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tools Section */}
          <div 
            className="flex-1 overflow-y-auto" 
            ref={scrollContainerRef}
          >
            <div className="p-4 border-b border-gray-700">
              {!selectedTool ? (
                <ToolSelector
                  onToolSelect={handleToolSelect}
                  featureFlags={featureFlags}
                />
              ) : (
                // Show active tool controls
                <div>
                  {renderToolControls()}
                </div>
              )}
            </div>
          
            {/* Only show adjustments when no tool is selected */}
            {!selectedTool && featureFlagSliders && (
              <div className="p-4">
                <div className="space-y-6">
                  {/* Exposure */}
                  <AdjustmentSlider
                    label="Exposure"
                    value={adjustments.exposure}
                    onChange={(value) => handleAdjustmentChange('exposure', value)}
                  />
                  
                  {/* Contrast */}
                  <AdjustmentSlider
                    label="Contrast"
                    value={adjustments.contrast}
                    onChange={(value) => handleAdjustmentChange('contrast', value)}
                  />
                  
                  {/* Highlights */}
                  <AdjustmentSlider
                    label="Highlights"
                    value={adjustments.highlights}
                    onChange={(value) => handleAdjustmentChange('highlights', value)}
                  />
                  
                  {/* Shadows */}
                  <AdjustmentSlider
                    label="Shadows"
                    value={adjustments.shadows}
                    onChange={(value) => handleAdjustmentChange('shadows', value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Version History */}
          <VersionHistory
            versions={versions}
            showVersions={showVersions}
            onToggleVersions={setShowVersions}
            onVersionSelect={handleVersionSelect}
            onSaveVersion={handleSaveVersion}
            isProcessing={isProcessing}
            isPainting={isPainting}
            featureFlagSaveVersion={featureFlagSaveVersion}
          />
        </div>
      </div>

      {/* Add confirmation modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Photo"
        message="Are you sure you want to delete this photo? This will delete all versions and cannot be undone."
        confirmText="Delete"
        confirmStyle="danger"
        confirmButtonText="Delete Photo"
      />

      {createdMaskId && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-gray-200">Generated Mask</h4>
          <MaskPreview maskId={createdMaskId} />
          <button
            onClick={() => {
              setCreatedMaskId(null)
              setSelectedTool(null)
            }}
            className="w-full p-2 bg-gray-700 rounded hover:bg-gray-600 mt-4"
          >
            Done
          </button>
        </div>
      )}
    </>
  )
}

export default RightPanel 